import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Trophy, Zap } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({ email: "", password: "", name: "", role: "parent" });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post("/auth/login", loginData);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('userRole', response.data.user.role);
      localStorage.setItem('userId', response.data.user.id);
      localStorage.setItem('userName', response.data.user.name);

      if (response.data.is_first_login) {
        localStorage.setItem('isFirstLogin', 'true');
        toast.success("Login successful! Please secure your account.");
        navigate("/change-password");
        return;
      }

      toast.success(`Welcome back, ${response.data.user.name}!`);
      navigate(response.data.user.role === 'coach' ? '/coach' : '/parent');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post("/auth/register", registerData);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('userRole', response.data.user.role);
      localStorage.setItem('userId', response.data.user.id);
      localStorage.setItem('userName', response.data.user.name);
      toast.success("Account created successfully!");
      navigate(response.data.user.role === 'coach' ? '/coach' : '/parent');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 animated-gradient">
      <div className="w-full max-w-md">
        <div className="text-center mb-10 fade-in">
          <img src="/gfa-logo.png?v=5" alt="German Football Academy" className="w-32 h-32 mx-auto mb-6 object-contain rounded-full bg-white p-1 drop-shadow-2xl" />
          <h1 className="text-5xl font-heading font-bold text-white mb-3 glow-text tracking-tight" data-testid="app-title">German Football Academy</h1>
          <p className="text-slate-300 text-lg mb-5">Track Progress, Build Champions</p>
          <div className="inline-flex items-center gap-2 stadia-badge px-5 py-2.5 rounded-full shadow-xl">
            <Zap className="w-5 h-5 text-white" />
            <span className="text-sm text-white font-bold tracking-wide">POWERED BY STADIA</span>
          </div>
        </div>

        <Card className="shadow-2xl border-0 fade-in" data-testid="login-card" style={{ animationDelay: '0.2s' }}>
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-slate-100">
              <TabsTrigger value="login" data-testid="login-tab" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">Login</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">Register</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <CardHeader>
                <CardTitle className="text-2xl font-heading">Welcome Back</CardTitle>
                <CardDescription>Enter your credentials to continue</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-base">Email</Label>
                    <Input
                      id="login-email"
                      data-testid="login-email-input"
                      type="email"
                      placeholder="coach@godspeed.com"
                      className="h-12"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="text-base">Password</Label>
                    <Input
                      id="login-password"
                      data-testid="login-password-input"
                      type="password"
                      className="h-12"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full h-12 text-base font-semibold bg-slate-900 hover:bg-slate-800" data-testid="login-submit-btn" disabled={loading}>
                    {loading ? "Logging in..." : "Login"}
                  </Button>
                </form>
              </CardContent>
            </TabsContent>

            <TabsContent value="register">
              <CardHeader>
                <CardTitle className="text-2xl font-heading">Create Account</CardTitle>
                <CardDescription>Join German Football Academy today</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRegister} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="register-name" className="text-base">Full Name</Label>
                    <Input
                      id="register-name"
                      data-testid="register-name-input"
                      placeholder="John Doe"
                      className="h-12"
                      value={registerData.name}
                      onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email" className="text-base">Email</Label>
                    <Input
                      id="register-email"
                      data-testid="register-email-input"
                      type="email"
                      placeholder="your@email.com"
                      className="h-12"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password" className="text-base">Password</Label>
                    <Input
                      id="register-password"
                      data-testid="register-password-input"
                      type="password"
                      className="h-12"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      required
                    />
                  </div>
                  <p className="text-sm text-slate-500 bg-slate-100 p-3 rounded-lg">
                    Parents can register here to view their child's report cards. Coaches are added by the academy administrator.
                  </p>
                  <Button type="submit" className="w-full h-12 text-base font-semibold bg-slate-900 hover:bg-slate-800" data-testid="register-submit-btn" disabled={loading}>
                    {loading ? "Creating Account..." : "Register as Parent"}
                  </Button>
                </form>
              </CardContent>
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
}